import { EventEmitter, Injectable, Output } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { AsgardDocFroala } from '../../core/documents/asgard-doc-froala';
import { AsgardDocBase } from '../../core/documents/asgard-doc-base';
import { ApiNamesEnum, FileActionEnum, MimeTypesEnum, ToastrActionEnum, ToastrStringsEnum } from '../../utils/enums';
import * as FileSaver from 'file-saver';
import { ToastrDialogService } from '../toastr-dialog.service';
import { TabService } from '../tab.service';
import { DocumentManagerService } from '../document/document-manager.service';
import { BlobToPdfService } from '../blob-to-pdf.service';
import { AccountService } from '../auth/account.service';
import { ModalWindowService } from '../ui/modal-window.service';
// import { CommonMessageDialogComponent } from '../../components/dialogs/common-message-dialog/common-message-dialog.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { AutosaveManagerService } from '../autosave/autosave-manager.service';
import { BaseConversion } from './conversions/base-conversion';
import { ConversionOptions } from './models/conversion-options';
import { ConvertorParams } from './models/convertor-params';
import { CurrentExamService } from '../exam/current-exam.service';

@Injectable({
  providedIn: 'root'
})

export class FileConvertorService {
  @Output() FileReady: EventEmitter<FileReadyEvent> = new EventEmitter();
  @Output() FileReadyForUpload: EventEmitter<File> = new EventEmitter();
  @Output() FileReadyForDL: EventEmitter<File> = new EventEmitter();

  // private get activeTab(): AsgardTab | null {
  //   return this.tabService.activeTab;
  // }

  private get currentDoc(): AsgardDocBase | null | undefined {
    return this.docMgrService.currentDoc;
  }

  htmlRtfUrl: string = environment.conversions.htmlRtfUrl;
  kesPdfUrl: string = environment.conversions.kesPdfUrl;
  imgPdfUrl: string = environment.conversions.imgPdfUrl;
  reader: FileReader = new FileReader();

  // TODO: This code is a mess. Needs refactor when I have time
  // Reinstate signalR and get it working
  // Make the service fully generic for both rtf & kes without passing everything from call to call
  // I have some ideas, bear with me while I fix this atrocity. Feedback ofcourse very much welcome.

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private tabService: TabService,
    private blobToPdfService: BlobToPdfService,
    private toastrService: ToastrDialogService,
    private readonly docMgrService: DocumentManagerService,
    private accountService: AccountService,
    private modalService: ModalWindowService,
    private readonly autosave: AutosaveManagerService,
    private readonly examService: CurrentExamService,
  ) {
    this.addEventListeners();
    this.subToFileReady();
  }

  addEventListeners(): void {
    this.reader.addEventListener('load', () => {
      if (this.reader.result) {
        (this.currentDoc as AsgardDocFroala).setHtmlContent(this.reader.result.toString());
      }
    });
  }

  subToFileReady(): void {
    this.FileReady.subscribe((e: FileReadyEvent) => {
      const file = e.file;
      const tabName = e.tabName || e.file.name;
      const fileHash = e.fileHash;
      if (file.type === 'text/html') {
        let newTab = this.tabService.addNewTab(tabName, true, e.paneIndex);
        if (e.metadata) {
          newTab.setMetadata(e.metadata);
        }
        const documentSub = this.docMgrService.documentReady.subscribe(x => {
          // console.warn('DOCUMENT READY: ', x);
          if (x && x === newTab.id) {
            this.setHtml(file, tabName, fileHash || null, newTab.id);
            documentSub.unsubscribe();
          }
        });
      }
    });
  }

  async makeConversion<T extends BaseConversion>(type: (new (args: [FileConvertorService, AccountService, BlobToPdfService]) => T),
    options: ConversionOptions): Promise<void> {
    const convertor = new type([this, this.accountService, this.blobToPdfService]);
    await convertor.setOptions(options);
    const params = convertor.prepareConversionParams();
    if (params !== null) {
      this.fileConvertor(params);
    }
  }

  async silentMakeConversion<T extends BaseConversion>(type: (new (args: [FileConvertorService, AccountService, BlobToPdfService]) => T),
    options: ConversionOptions): Promise<void> {
    const convertor = new type([this, this.accountService, this.blobToPdfService]);
    await convertor.setOptions(options);
    const params = convertor.prepareConversionParams();
    if (params !== null) {
      this.silentConversion(params);
    }
  }

  public getHtml(): string {
    if (this.currentDoc) {
      this.currentDoc.clearTextHighlight(true);
      if (this.currentDoc instanceof AsgardDocFroala) {
        return this.currentDoc.getHtmlContent() || '';
      }
    }

    return '';
  }

  private async setHtml(file: File, tabName: string, fileHash: string | null, docId: string | null = null): Promise<void> {
    if (docId === null) {
      return;
    }

    let doc = this.docMgrService.getDocumentById(docId);
    let tab = this.tabService.getTabById(docId);
    if (doc && tab) {
      tab.originalFileName = tabName;
      try {
        const validTitle = await this.tabService.validateTitle(tabName, tab);
        tab.setTabTitle(validTitle);
      } catch {
        this.tabService.closeTab(tab.id);
      }

      doc.docHash = fileHash;
      this.autosave.addOriginalFile(fileHash, null, tabName, null, this.examService.getDocDocumentType(docId));

      const reader: FileReader = new FileReader();
      reader.onload = (ev: ProgressEvent<FileReader>) => {
        // console.log(ev);
        if (doc && ev.target && ev.target.result) {
          (doc as AsgardDocFroala).setHtmlContent(ev.target.result.toString());
        }
      }
      reader.readAsText(file);
    }

    // if (this.activeTab && this.currentDoc) {
    //   this.activeTab.originalFileName = tabName;
    //   this.tabService.setTitleForActiveTab(tabName);

    //   this.currentDoc.docHash = fileHash;
    //   /* store initial file state */
    //   // const loadend = async (e: ProgressEvent) => {
    //   //   await this.autosave.addOriginalFile(fileHash, null, tabName, (e as any).target.result);
    //   // }
    //   // this.reader.onloadend = loadend;
    //   this.autosave.addOriginalFile(fileHash, null, tabName);
    //   this.reader.readAsText(file);
    // }
  }

  private async fileConvertor(conversionParams: ConvertorParams): Promise<void> {
    const { action, url, formData, fileHash, params, type, fileName, readingItemIdx, paneIndex, metadata } = conversionParams;

    this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Converting, true, fileName);
    this.modalService.createModalWindow(SpinnerComponent);

    const authInfo = this.authService.getAuthInfo();

    if (authInfo) {
      const headers = new HttpHeaders();
      headers.set('Authorization', `Bearer ${authInfo.access_token}`);

      const options = {
        headers,
        params: params as { [param: string]: string | string[]; }
      };
      const response: any = await this.http.post(url, formData, options)
        .toPromise()
        .catch((err) => {
          console.log(err);
          // const error = err as HttpErrorResponse;
          this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.ConversionFailed, false, fileName);
          // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: error.error.errorMessage});
        });

      if (response !== undefined) {
        this.getStatus(response.id, type, fileName, url, action, fileHash || null, readingItemIdx, paneIndex, metadata);
      }
    }

    // close the 'Converting ...' toastr
    this.toastrService.hideToastr(fileName);
  }

  private async silentConversion(conversionParams: ConvertorParams): Promise<void> {
    const {url, formData } = conversionParams;

    const response: any = await this.http.post(url, formData).toPromise();

    if (response !== undefined) {
      this.silentGetStatus(response.id, url);
    }
  }

  private async getStatus(id: string, targetType: MimeTypesEnum, name: string, url: string, action: FileActionEnum,
    fileHash: string | null, readingItemIdx: number | null = null, paneIndex: number | null = null, metadata: any = null): Promise<void> {
    let isKES = false;
    let isPDF = false;

    if (name && name.match(/(\.kes|\.sek)$/i)) {
      isKES = true;
    }
    if (name && name.match(/(\.pdf)$/i)) {
      isPDF = true;
    }
    const authInfo = this.authService.getAuthInfo();

    if (authInfo) {
      const header = {
        headers: new HttpHeaders()
          .set('Authorization', `Bearer ${authInfo.access_token}`)
      };
      const response: any = await this.http.get(url + id + '/status', header)
        .toPromise()
        .catch((err) => console.log(err));

      if (response.status === 4) {
        await this.getFile(id, name, url, action, fileHash, readingItemIdx, paneIndex, metadata);
        if (isKES && action === FileActionEnum.ImportKes) {
          // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.KES_CONVERSION_SUCCESS' });
          this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.ConversionDone);
        } else {
          this.modalService.closeSpinner();
        }
      } else if (response.status === 3) {
        this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.ConversionFailed, false, name);
        if (response.errorCode >= 206 && response.errorCode <= 212 && (isKES || isPDF)) {
          // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: `HUGINN_ERRORS.${response.errorCode}` });
          this.toastrService.showApiErrorToastr(response.errorCode, ApiNamesEnum.Huginn, 500);
        } else {
          this.modalService.closeSpinner();
        }
      } else {
        await this.sleep(1000);
        this.getStatus(id, targetType, name, url, action, fileHash, paneIndex, metadata);
      }
    }
  }

  private async silentGetStatus(id: string, url: string): Promise<void> {
      const response: any = await this.http.get(url + id + '/status')
        .toPromise()
        .catch((err) => console.log(err));

      if (response.status === 4) {
        await this.silentGetFile(id, url);
      } else if (response.status === 3) {
        throw new Error('failed conversion');
      } else {
        await this.sleep(1000);
        this.silentGetStatus(id, url);
      }
    }

  private async getFile(id: string, tabName: string | undefined, url: string, action: FileActionEnum,
    fileHash: string | null, readingItemIdx: number | null = null, paneIndex: number | null = null, metadata: any = null): Promise<void> {
    const authInfo = this.authService.getAuthInfo();

    if (authInfo) {
      const options = {
        headers: new HttpHeaders().set('Authorization', `Bearer ${authInfo.access_token}`),
        responseType: 'arraybuffer',
        observe: 'response'
      };

      // @ts-ignore
      const response: HttpResponse = await this.http.get(url + id + '/file', options)
        .toPromise()
        .catch((err) => console.log(err));

      const name = this.getFileNameFromResponseContentDisposition(response);

      const convertedFile = new File(
        [response.body],
        name,
        { type: response.headers.get('content-type') }
      );

      this.processConvertedFile(action, convertedFile, tabName, fileHash, readingItemIdx, paneIndex, metadata);
      this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.ConversionDone, false, tabName);
    }
  }

  private async silentGetFile(id: string, url: string): Promise<void> {
      const options = {
        responseType: 'arraybuffer',
        observe: 'response'
      };

      // @ts-ignore
      const response: HttpResponse = await this.http.get(url + id + '/file', options)
        .toPromise()
        .catch((err) => console.log(err));

      const name = this.getFileNameFromResponseContentDisposition(response);

      const convertedFile = new File(
        [response.body],
        name,
        { type: response.headers.get('content-type') }
      );

      this.FileReady.emit({ file: convertedFile });
  }

  private getFileNameFromResponseContentDisposition(res: Response): string {
    const contentDisposition = res.headers.get('content-disposition') || '';
    const matches = /filename=([^;]+)/ig.exec(contentDisposition);
    let fileName = (matches && matches[1] ? matches[1] : 'untitled').trim();
    fileName = fileName.replace(/"+$/, '').replace(/^"+/, '');
    const encodedFileNameMatch = fileName.match(/^=\?utf-8\?B\?(.*)\?=$/);

    if (encodedFileNameMatch) {
      fileName = this.base64ToUTF8(encodedFileNameMatch[1]);
    }

    return fileName;
  }

  private base64ToUTF8(str: string): string {
    return decodeURIComponent(atob(str).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  private async processConvertedFile(action: FileActionEnum, convertedFile: File, tabName: string | null = null,
    fileHash: string | null, readingItemIdx: number | null = null, paneIndex: number | null = null, metadata: any = null): Promise<void> {
    switch (action) {
      case FileActionEnum.Download:
      case FileActionEnum.ExportKes:
      case FileActionEnum.UploadToDrive:
        await this.autosave.addSavedFileAndUpdateReadingPos(convertedFile, readingItemIdx);
        break;
      default: break;
    }

    switch (action) {
      case FileActionEnum.UploadToDrive:
        this.FileReadyForUpload.emit(convertedFile);
        break;
      case FileActionEnum.Download:
        this.FileReadyForDL.emit(convertedFile);
        break;
      case FileActionEnum.ImportKes:
      case FileActionEnum.ConvertImage:
        this.tabService.setTabFile(convertedFile, tabName, paneIndex);
        break;
      case FileActionEnum.ExportKes:
        FileSaver.saveAs(convertedFile, convertedFile.name);
        break;
      case FileActionEnum.InsertHtml:
        this.FileReady.emit({ file: convertedFile, tabName, fileHash, paneIndex, metadata });
        break;
      default: break;
    }
  }

  private sleep(ms: number): Promise<{}> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export interface FileReadyEvent {
  file: File;
  tabName?: string | null;
  fileHash?: string | null;
  paneIndex?: number | null;
  metadata?: any;
}
